import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";
import { C } from "../lib/constants";
import { Btn } from "./ui/Btn";
import { Card } from "./ui/Card";
import { Chip } from "./ui/Chip";
import { TopNav } from "./ui/TopNav";
import { getFlowInfo, EstadoChip, FlowTimeline, MediationBox, EmptyBox, MediacionModal } from "./WorkFlow";
import { ClienteLoginScreen } from "./AuthScreens";
import { InfoMini } from "./PerfilCliente";

export const PortalCliente = ({setScreen, user, profile, onLogout}) => {
  const [cliente,          setCliente]          = useState(null);
  const [trabajos,         setTrabajos]         = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [err,              setErr]              = useState("");
  const [mediacionTarget,  setMediacionTarget]  = useState(null);
  const [mediacionReason,  setMediacionReason]  = useState("");

  const userId   = user?.id || null;

  const cargar = useCallback(async () => {
    setErr("");
    if (!userId) { setLoading(false); return; }
    setLoading(true);

    // Cargar perfil de cliente (sin upsert en cargar — se hace una sola vez en mount)
    const { data: clienteData, error: clienteError } = await supabase
      .from("clientes").select("*").eq("user_id", userId).maybeSingle();
    if (clienteError) console.warn("Error cargando cliente:", clienteError.message);
    setCliente(clienteData || null);

    // FIX SESIÓN CRUZADA: SOLO filtrar solicitudes por cliente_user_id.
    // Eliminado cliente_telefono del filtro — podía retornar solicitudes de otro cliente.
    const { data: solicitudes, error: solError } = await supabase
      .from("solicitudes").select("*")
      .eq("cliente_user_id", userId)   // ESTRICTO: solo user_id
      .order("created_at", { ascending: false });

    if (solError) {
      setErr(solError.message);
      setTrabajos([]);
    } else {
      setTrabajos(solicitudes || []);
    }
    setLoading(false);
  // FIX SESIÓN CRUZADA: userPhone eliminado de deps — ya no se usa en la query
  }, [userId]);

  useEffect(() => {
    cargar();
    // Canal filtrado — solo solicitudes de este usuario
    const chName = `portal-cliente-${userId || "anon"}`;
    const channel = supabase.channel(chName)
      .on("postgres_changes", {
        event: "*", schema: "public", table: "solicitudes",
        filter: userId ? `cliente_user_id=eq.${userId}` : undefined
      }, cargar)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [cargar, userId]);

  const actualizarTrabajo = async (id, estado, extra={}) => {
    const payload = { estado, updated_at: new Date().toISOString(), ...extra };
    if (estado === "aceptada")            { payload.cliente_autorizo = true; payload.authorized_at = new Date().toISOString(); }
    if (estado === "completada")          { payload.cliente_concluyo = true; payload.finished_at = new Date().toISOString(); payload.concluded_by = "cliente"; }
    if (estado === "mediacion_solicitada"){ payload.mediation_requested_by = "cliente"; payload.mediation_fee = 500; payload.mediation_requested_at = new Date().toISOString(); }
    const { error } = await supabase.from("solicitudes").update(payload).eq("id", id);
    if (error) { console.error("Error actualizando trabajo:", error.message); setErr("Error: " + error.message); return; }
    cargar();
  };

  const solicitarMediacionPortal = async () => {
    if (!mediacionTarget) return;
    // FIX E6: return duplicado eliminado
    if (!mediacionReason.trim()) { window.ficioToast?.("Escribe el motivo de la mediación", "warn"); return; }
    await actualizarTrabajo(mediacionTarget.id, "mediacion_solicitada", { mediation_reason: mediacionReason.trim() });
    setMediacionTarget(null);
    setMediacionReason("");
  };

  if (!user) return <ClienteLoginScreen setScreen={setScreen} onAuthReady={()=>{}}/>;

  const stats = {
    total:      trabajos.length,
    cotizadas:  trabajos.filter(t => t.estado === "pendiente").length,
    autorizadas:trabajos.filter(t => ["aceptada","en_progreso"].includes(t.estado)).length,
    mediaciones:trabajos.filter(t => ["mediacion_solicitada","en_mediacion"].includes(t.estado||t.status)).length,
  };

  return (
    <div style={{minHeight:"100vh",background:C.bg,paddingBottom:100}}>
      <TopNav setScreen={setScreen} back="landing" backLabel="Inicio" title="Portal Cliente"/>

      <div style={{maxWidth:680,margin:"0 auto",padding:"20px"}}>
        <div style={{background:`linear-gradient(135deg,${C.blue},#123E73)`,borderRadius:24,padding:22,color:"#fff",marginBottom:16,boxShadow:"0 8px 28px rgba(37,88,168,.25)"}}>
          <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"center"}}>
            <div>
              <div style={{fontSize:30,marginBottom:6}}>🏠</div>
              <div style={{fontFamily:"Georgia, serif",fontSize:26,fontWeight:900}}>
                Hola, {cliente?.nombre || profile?.nombre || "cliente"}
              </div>
              <div style={{fontSize:13,opacity:.88}}>Tus trabajos, cotizaciones y mediaciones en un solo lugar.</div>
            </div>
            <Chip text="Cliente" color={C.green} small/>
          </div>
        </div>

        {err && <div style={{background:C.redL,border:`1px solid ${C.red}35`,borderRadius:14,padding:"12px",color:C.red,fontSize:12,marginBottom:14}}>{err}</div>}

        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:14}}>
          <InfoMini label="Total"      value={stats.total}/>
          <InfoMini label="Cotizadas"  value={stats.cotizadas}/>
          <InfoMini label="Activas"    value={stats.autorizadas}/>
          <InfoMini label="Mediación"  value={stats.mediaciones}/>
        </div>

        <Card>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
            <Btn full color={C.blue}          onClick={()=>setScreen("directory")}>🔍 Buscar maestro</Btn>
            <Btn full outline color={C.green} onClick={()=>setScreen("perfil-cliente")}>👤 Mi perfil</Btn>
            <Btn full outline color={C.red}   onClick={onLogout}>Salir</Btn>
          </div>
        </Card>

        <div style={{fontFamily:"Georgia, serif",fontSize:22,fontWeight:900,color:C.text,margin:"18px 0 10px"}}>
          Mis trabajos
        </div>

        {loading ? (
          <div style={{textAlign:"center",padding:"40px",color:C.muted}}>Cargando trabajos...</div>
        ) : trabajos.length === 0 ? (
          <EmptyBox icon="📭" title="Aún no tienes trabajos" desc="Cuando solicites una cotización aparecerá aquí."/>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {trabajos.map(t => {
              const info          = getFlowInfo(t.estado || t.status || "pendiente");
              const puedeAutorizar = (t.estado || t.status || "pendiente") === "pendiente";
              const puedeConcluir  = (t.estado || t.status) === "en_progreso";

              return (
                <div key={t.id} style={{background:C.surface,border:`1.5px solid ${info.color}30`,borderRadius:18,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",marginBottom:10}}>
                    <div>
                      <div style={{fontFamily:"Georgia, serif",fontSize:18,fontWeight:900,color:C.text}}>{t.trabajador_nombre || "Trabajador"}</div>
                      <div style={{color:C.muted,fontSize:12}}>📍 {t.zona || "Sin zona"} · {t.oficio || "Servicio"}</div>
                    </div>
                    <EstadoChip status={t.estado || t.status || "pendiente"}/>
                  </div>
                  <FlowTimeline status={t.estado || t.status || "pendiente"}/>
                  <MediationBox solicitud={t}/>
                  <div style={{background:C.bg,borderRadius:14,padding:13,marginBottom:12,color:C.sub,fontSize:13,lineHeight:1.5}}>{t.descripcion}</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {puedeAutorizar ? (
                      <Btn small full color={C.blue}  onClick={()=>actualizarTrabajo(t.id, "aceptada")}>Autorizar trabajo</Btn>
                    ) : puedeConcluir ? (
                      <Btn small full color={C.green} onClick={()=>actualizarTrabajo(t.id, "completada", {concluded_by:"cliente"})}>Concluir trabajo</Btn>
                    ) : (
                      <Btn small full outline color={info.color} onClick={()=>window.ficioToast?.(`Estado actual: ${info.label}`, "info")}>{info.label}</Btn>
                    )}
                    <Btn small full outline color={C.red} onClick={()=>setMediacionTarget(t)}>⚖️ Mediación $500</Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {mediacionTarget && (
        <MediacionModal
          solicitud={mediacionTarget}
          reason={mediacionReason}
          setReason={setMediacionReason}
          onClose={()=>{setMediacionTarget(null);setMediacionReason("");}}
          onConfirm={solicitarMediacionPortal}
        />
      )}
    </div>
  );
};
